// src/pages/Seller/Shipments.jsx
import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Avatar,
  Tooltip,
  LinearProgress,
  Alert,
  Divider,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import {
  LocalShipping,
  Search,
  Refresh,
  Visibility,
  FlightTakeoff,
  Anchor,
  CheckCircle,
  Schedule,
  OpenInNew,
  Assignment,
  Person,
  CalendarToday,
  Info,
} from '@mui/icons-material';
import { sellerAPI } from '../../services/api';
import { formatDate } from '../../utils/helpers';
import toast from 'react-hot-toast';

// ─── Shipment status steps ────────────────────────────────────────────────────
const SHIPMENT_STEPS = [
  { key: 'booked',      label: 'Booked',       icon: <Assignment /> },
  { key: 'in_transit',  label: 'In Transit',   icon: <LocalShipping /> },
  { key: 'at_port',     label: 'At Port',      icon: <Anchor /> },
  { key: 'out_for_delivery', label: 'Out for Delivery', icon: <FlightTakeoff /> },
  { key: 'delivered',   label: 'Delivered',    icon: <CheckCircle /> },
];

const STATUS_ORDER = ['booked', 'in_transit', 'at_port', 'out_for_delivery', 'delivered'];

const getStatusIndex = (status) => {
  const s = (status || '').toLowerCase().replace(/ /g, '_');
  const idx = STATUS_ORDER.indexOf(s);
  return idx >= 0 ? idx : 0;
};

const getStatusColor = (status) => {
  switch ((status || '').toLowerCase().replace(/ /g, '_')) {
    case 'delivered':        return 'success';
    case 'in_transit':       return 'info';
    case 'out_for_delivery': return 'warning';
    case 'at_port':          return 'secondary';
    case 'cancelled':        return 'error';
    default:                 return 'default';
  }
};

// ─── Shipment detail dialog content ──────────────────────────────────────────
const ShipmentDetail = ({ shipment }) => {
  const stepIdx = getStatusIndex(shipment.status);

  const fields = [
    ['Shipment ID',       shipment.shipment_id || shipment.id],
    ['Enquiry / Order',   shipment.enquiry_id],
    ['Buyer',             shipment.buyer_name || shipment.buyerName],
    ['Product',           shipment.product || shipment.productName],
    ['Vessel / Flight',   shipment.vessel_name],
    ['Container No.',     shipment.container_number],
    ['Bill of Lading',    shipment.bill_of_lading],
    ['Port of Loading',   shipment.port_of_loading],
    ['Port of Discharge', shipment.port_of_discharge],
    ['Departure Date',    shipment.departure_date ? formatDate(shipment.departure_date) : null],
    ['Arrival Date',      shipment.arrival_date   ? formatDate(shipment.arrival_date)   : null],
    ['Carrier',           shipment.carrier],
    ['Notes',             shipment.notes],
  ];

  return (
    <Box>
      {/* Progress tracker */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
          Shipment Progress
        </Typography>
        <Stepper activeStep={stepIdx} alternativeLabel sx={{ mt: 1 }}>
          {SHIPMENT_STEPS.map((step, i) => (
            <Step key={step.key} completed={i < stepIdx}>
              <StepLabel
                StepIconComponent={() => (
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: i <= stepIdx ? 'primary.main' : 'grey.300',
                      color: i <= stepIdx ? 'white' : 'grey.600',
                    }}
                  >
                    {React.cloneElement(step.icon, { sx: { fontSize: 16 } })}
                  </Avatar>
                )}
              >
                <Typography variant="caption">{step.label}</Typography>
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {/* Current status chip */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" color="text.secondary">Current Status:</Typography>
        <Chip
          label={(shipment.status || 'booked').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
          color={getStatusColor(shipment.status)}
          size="small"
        />
      </Box>

      {/* All detail fields */}
      <Grid container spacing={2}>
        {fields.map(([label, value]) =>
          value ? (
            <Grid item xs={12} sm={6} key={label}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 130 }}>
                  {label}:
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {value}
                </Typography>
              </Box>
            </Grid>
          ) : null
        )}
      </Grid>

      {/* Track online button */}
      {shipment.tracking_url && (
        <Box sx={{ mt: 3 }}>
          <Button
            variant="contained"
            startIcon={<OpenInNew />}
            href={shipment.tracking_url}
            target="_blank"
            rel="noopener noreferrer"
            fullWidth
          >
            Track Shipment Online
          </Button>
        </Box>
      )}

      {/* Arrival countdown */}
      {shipment.arrival_date && shipment.status !== 'delivered' && (
        <Alert severity="info" icon={<CalendarToday />} sx={{ mt: 2 }}>
          Expected arrival: <strong>{formatDate(shipment.arrival_date)}</strong>
          {(() => {
            const days = Math.ceil((new Date(shipment.arrival_date) - new Date()) / 86400000);
            return days > 0 ? ` (${days} day${days === 1 ? '' : 's'} remaining)` : ' (today or past due)';
          })()}
        </Alert>
      )}
    </Box>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
const SellerShipments = () => {
  const [shipments, setShipments]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selected, setSelected]     = useState(null);

  useEffect(() => { fetchShipments(); }, []);

  const fetchShipments = async () => {
    try {
      setLoading(true);
      const res = await sellerAPI.getMyShipments();
      const data = Array.isArray(res.data)
        ? res.data
        : res.data?.shipments || [];
      setShipments(data);
    } catch (err) {
      console.error('Error fetching shipments:', err);
      toast.error('Failed to load shipments');
    } finally {
      setLoading(false);
    }
  };

  // Stats
  const stats = {
    total:    shipments.length,
    transit:  shipments.filter(s => ['in_transit', 'at_port', 'out_for_delivery'].includes((s.status || '').toLowerCase().replace(/ /g, '_'))).length,
    delivered: shipments.filter(s => (s.status || '').toLowerCase() === 'delivered').length,
    booked:   shipments.filter(s => (s.status || '').toLowerCase() === 'booked').length,
  };

  const filtered = shipments.filter(s => {
    const q = searchTerm.toLowerCase();
    return (
      (s.shipment_id || '').toLowerCase().includes(q) ||
      (s.vessel_name || '').toLowerCase().includes(q) ||
      (s.container_number || '').toLowerCase().includes(q) ||
      (s.port_of_discharge || '').toLowerCase().includes(q) ||
      (s.product || s.productName || '').toLowerCase().includes(q) ||
      (s.buyer_name || s.buyerName || '').toLowerCase().includes(q)
    );
  });

  if (loading) {
    return (
      <Container sx={{ py: 4, textAlign: 'center' }}>
        <LinearProgress sx={{ mb: 2 }} />
        <Typography>Loading shipments...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Shipment Tracking
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Track all shipments assigned to your orders
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Refresh />} onClick={fetchShipments}>
          Refresh
        </Button>
      </Box>

      {/* Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {[
          { label: 'Total Shipments',  value: stats.total,     color: '#2196f3', icon: <LocalShipping /> },
          { label: 'In Transit',       value: stats.transit,   color: '#ff9800', icon: <FlightTakeoff /> },
          { label: 'Delivered',        value: stats.delivered, color: '#4caf50', icon: <CheckCircle /> },
          { label: 'Booked',           value: stats.booked,    color: '#9c27b0', icon: <Schedule /> },
        ].map((stat, i) => (
          <Grid item xs={6} sm={3} key={i}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="textSecondary" variant="body2">{stat.label}</Typography>
                    <Typography variant="h4" fontWeight="bold">{stat.value}</Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: `${stat.color}18`, color: stat.color }}>
                    {stat.icon}
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Search */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <TextField
          fullWidth
          placeholder="Search by shipment ID, vessel, container, port, product or buyer..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><Search /></InputAdornment>
            ),
          }}
          size="small"
        />
      </Paper>

      {/* Table */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: 'grey.50' }}>
              <TableRow>
                {['Shipment ID', 'Order / Product', 'Buyer', 'Vessel / Container', 'Port of Discharge', 'Arrival', 'Status', 'Actions'].map(h => (
                  <TableCell key={h}>
                    <Typography variant="subtitle2" fontWeight="bold">{h}</Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                    <LocalShipping sx={{ fontSize: 60, color: 'text.secondary', mb: 2, display: 'block', mx: 'auto' }} />
                    <Typography variant="h6" color="text.secondary">
                      {searchTerm ? 'No shipments match your search' : 'No shipments yet'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {!searchTerm && 'Shipments assigned to your orders by admin will appear here.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((s, idx) => (
                  <TableRow key={s.id || s.shipment_id || idx} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {s.shipment_id || `#${s.id}`}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {s.product || s.productName || '—'}
                      </Typography>
                      {s.enquiry_id && (
                        <Typography variant="caption" color="text.secondary">
                          Order #{s.enquiry_id}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 28, height: 28, fontSize: '0.75rem' }}>
                          {(s.buyer_name || s.buyerName || 'B').charAt(0).toUpperCase()}
                        </Avatar>
                        <Typography variant="body2">
                          {s.buyer_name || s.buyerName || '—'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {s.vessel_name && (
                        <Typography variant="body2">{s.vessel_name}</Typography>
                      )}
                      {s.container_number && (
                        <Typography variant="caption" color="text.secondary">
                          {s.container_number}
                        </Typography>
                      )}
                      {!s.vessel_name && !s.container_number && (
                        <Typography variant="body2" color="text.secondary">—</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {s.port_of_discharge || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {s.arrival_date ? formatDate(s.arrival_date) : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={(s.status || 'booked').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                        color={getStatusColor(s.status)}
                        size="small"
                        sx={{ textTransform: 'capitalize' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title="View Details">
                        <IconButton size="small" color="primary" onClick={() => setSelected(s)}>
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      {s.tracking_url && (
                        <Tooltip title="Track Online">
                          <IconButton
                            size="small"
                            color="info"
                            component="a"
                            href={s.tracking_url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <OpenInNew />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Detail Dialog */}
      <Dialog open={Boolean(selected)} onClose={() => setSelected(null)} maxWidth="md" fullWidth>
        {selected && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocalShipping color="primary" />
                <Typography variant="h6">
                  Shipment {selected.shipment_id || `#${selected.id}`}
                </Typography>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Box sx={{ mt: 1 }}>
                <ShipmentDetail shipment={selected} />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelected(null)}>Close</Button>
              {selected.tracking_url && (
                <Button
                  variant="contained"
                  startIcon={<OpenInNew />}
                  href={selected.tracking_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Track Online
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>
    </Container>
  );
};

export default SellerShipments;
